import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Checkbox, Flex, Form, Input, InputNumber, Select, type FormInstance } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { FilterFunc } from "rc-select/lib/Select";
import type { DefaultOptionType } from "antd/es/cascader";
import { useMemo } from "react";
import { useWatch } from "antd/es/form/Form";
import { TASK_DESCRIPTION_MAX_LENGTH } from "../../../common/constants/constants";
import type { Blade, Services, TicketItem } from "../../generated-axios-client";
import { BladeUploadSection } from "./BladeUploadSection";
import { serviceSelectOptions } from "./ServiceSelectorFormItem";

const bladeSelectOptions = (bladeItems: Blade[]) =>
  bladeItems.map((item) => ({
    key: item.id,
    value: item.id,
    label: item.serial_number,
  }));

const bladeSearchHandler: FilterFunc<DefaultOptionType> = (input, option) => {
  const search = input.toLowerCase();
  const value = option?.label?.toString().toLowerCase?.() ?? "";
  return value.includes(search);
};

type TaskSelectorFormItemProps = {
  intl: IntlShape;
  bladeItems?: Blade[];
  serviceItems: Services[];
  form: FormInstance;
  showUpload?: boolean;
  dispatch?: Dispatch;
  notifficationApi?: HookAPI;
};

export const TaskSelectorFormItem = (props: TaskSelectorFormItemProps) => {
  const {
    intl,
    bladeItems,
    serviceItems,
    form,
    showUpload = false,
    dispatch,
    notifficationApi,
  } = props;

  const bladeOptions = useMemo(() => bladeSelectOptions(bladeItems ?? []), [bladeItems]);
  const serviceOptions = useMemo(() => serviceSelectOptions(serviceItems), [serviceItems]);

  const isTurbineSelected = useWatch("turbine", form);

  return (
    <Form.Item label={intl.formatMessage({ id: "ticket_tasks" })}>
      <Form.List
        name="items"
        rules={[
          {
            validator: async (_, items) => {
              if (!items?.length) {
                return Promise.reject(
                  intl.formatMessage({ id: "validation_message_ticket_tasks" })
                );
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Flex
                key={key}
                vertical
                gap="small"
                style={{
                  marginBottom: "var(--ant-margin-sm)",
                  padding: "var(--ant-padding-sm)",
                  border: "1px solid var(--ant-color-border)",
                  borderRadius: "var(--ant-border-radius)",
                }}
              >
                <Flex gap="small" align="flex-start">
                  <Flex vertical gap="small" style={{ flex: 1 }}>
                    <Flex gap="small">
                      <Form.Item
                        {...restField}
                        name={[name, "blade"]}
                        label={intl.formatMessage({ id: "ticket_task_blade" })}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: "validation_message_ticket_task_blade",
                            }),
                          },
                        ]}
                      >
                        <Select
                          options={bladeOptions}
                          showSearch
                          filterOption={bladeSearchHandler}
                          placeholder={intl.formatMessage({
                            id: "ticket_blade_select_placeholder",
                          })}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "service"]}
                        label={intl.formatMessage({ id: "ticket_task_service" })}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Select
                          options={serviceOptions}
                          showSearch
                          allowClear
                          placeholder={intl.formatMessage({
                            id: "ticket_task_service",
                          })}
                        />
                      </Form.Item>
                    </Flex>
                    {/* Keeps the default Form.Item bottom margin, unlike the fields
                        around it: the showCount counter renders below the TextArea and
                        needs that room, or it sits on top of the row beneath. */}
                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      label={intl.formatMessage({ id: "ticket_description" })}
                      rules={[
                        {
                          max: TASK_DESCRIPTION_MAX_LENGTH,
                          message: intl.formatMessage(
                            { id: "validation_message_max_length" },
                            { max: TASK_DESCRIPTION_MAX_LENGTH }
                          ),
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={2}
                        maxLength={TASK_DESCRIPTION_MAX_LENGTH}
                        showCount
                      />
                    </Form.Item>
                    <Flex gap="small" align="flex-end">
                      <Form.Item
                        {...restField}
                        name={[name, "estimated_time_spent"]}
                        label={intl.formatMessage({ id: "ticket_task_estimated_time" })}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <InputNumber
                          min={0}
                          step={0.5}
                          precision={1}
                          addonAfter="h"
                          placeholder="e.g. 2.5"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "completed"]}
                        label={intl.formatMessage({ id: "ticket_task_completed" })}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox />
                      </Form.Item>
                    </Flex>
                    {showUpload && dispatch && (
                      <Form.Item name={[name, "new_files"]} noStyle>
                        <BladeUploadSection
                          intl={intl}
                          dispatch={dispatch}
                          fieldPrefix={name}
                          notifficationApi={notifficationApi}
                        />
                      </Form.Item>
                    )}
                  </Flex>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                    style={{ marginTop: 24 }}
                  />
                </Flex>
                <Form.Item name={[name, "id"]} style={{ display: "none" }}>
                  <Input />
                </Form.Item>
                <Form.Item name={[name, "ticket"]} style={{ display: "none" }}>
                  <Input />
                </Form.Item>
              </Flex>
            ))}
            <Form.Item>
              <Button
                disabled={!isTurbineSelected}
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                {intl.formatMessage({ id: "ticket_button_add_task" })}
              </Button>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>
    </Form.Item>
  );
};
